// packages/components/src/showcase/IndustriesChips.tsx
import React, { useCallback } from 'react';
import { Button, Card, Tooltip } from '@dopomogai/ui';

export type IndustryChip = {
  key: string;
  title?: string;
  description?: string;
  default_tier?: 'trainee' | 'junior' | 'middle' | 'senior';
  copy_overrides?: { hero_title_i18n_key?: string };
};

type Props = {
  industries: IndustryChip[];
  selectedKey?: string;
  onSelect: (key?: string) => void; // passing undefined clears selection
  className?: string;
  showClear?: boolean;
  ariaLabel?: string;
};

export function IndustriesChips({
  industries,
  selectedKey,
  onSelect,
  className,
  showClear = true,
  ariaLabel = 'Industries',
}: Props) {
  if (!industries || industries.length === 0) return null;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        onSelect(undefined);
      }
    },
    [onSelect]
  );

  return (
    <Card className={className}>
      <div className="p-5">
        <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-900 border-2 border-black rounded-full px-3 py-1.5 mb-3">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-700" />
          <span className="text-xs font-bold tracking-wider">{ariaLabel}</span>
        </div>

        <div
          role="group"
          aria-label={ariaLabel}
          onKeyDown={handleKeyDown}
          className="flex flex-wrap gap-2"
        >
          {industries.map((pr) => {
            const active = selectedKey === pr.key;
            const chip = (
              <Button
                key={pr.key}
                variant={active ? 'primary' : 'outline'}
                onClick={() => onSelect(pr.key)}
                title={pr.description || pr.title || pr.key}
                text={pr.title || pr.key}
                aria-pressed={active}
                aria-controls="bundles"
              />
            );
            return pr.description ? (
              <Tooltip key={pr.key} text={pr.description}>{chip}</Tooltip>
            ) : (
              chip
            );
          })}
          {showClear && selectedKey && (
            <Button
              variant="ghost"
              onClick={() => onSelect(undefined)}
              title="Clear industry"
              text="Clear"
              aria-label="Clear selected industry"
            />
          )}
        </div>
      </div>
    </Card>
  );
}

export default IndustriesChips;
